
import { computed, defineComponent, PropType, Ref, ref } from 'vue';
import { Form } from 'vee-validate';
import { useBlockLabels } from '@/shared/content/blocks/helpers/block-labels.plugin';
import siteContextService from '@/b2c/shared/site-context.service';
import { LinkModel } from '@/types/B2CcontentServerContract';
import Recaptcha, { RecaptchaComponentInstance } from '@/shared/form/recaptcha/Recaptcha.vue';
import authService from '@/b2c/shared/auth/auth.service';
import WebApiMessage from '@/core/http/WebApiMessage.vue';
import { useRouteQuery } from '@/core/browser/route-query.composable';
import { QueryKeys } from '@/shared/config/route-query.config';
import dictionaryService from '@/core/dictionary/dictionary.service';
import { ClientResponse, LoginResponse } from '@/b2c/shared/api/server-apis';
import utilities from '@/shared/utilities.service';
import authStore from '@/shared/auth/auth.store';
import { maybeSoftRedirect } from '@/core/navigation/redirect.service';
import gtmService from '@/shared/tracking/gtm.service';

export const loginFormWebApiMessageKey = 'loginFormApiMessages';

export default defineComponent({
    name: 'LoginForm',
    components: { WebApiMessage, Form, Recaptcha },
    props: {
        title: {
            type: String,
            required: false,
            default: '',
        },
        redirectLink: {
            type: Object as PropType<LinkModel>,
            required: false,
            default: {
                url: siteContextService.frontPageUrl,
                spaLink: false,
            } as LinkModel,
        },
    },
    setup(props) {
        const labels = useBlockLabels();
        const isResetPasswordState: Ref<boolean> = ref(false);
        const recaptcha: Ref<RecaptchaComponentInstance | null> = ref(null);
        const recaptchaKey: Ref<string> = ref('');
        const pending: Ref<boolean> = ref(false);
        const showEmailSent: Ref<boolean> = ref(false);
        const returnTitle = useRouteQuery(QueryKeys.Title);
        const returnLink:LinkModel = {
            url: useRouteQuery(QueryKeys.ReturnUrl).value,
            spaLink: !useRouteQuery(QueryKeys.ReturnTypeHard).value,
        };

        const createProfileLink: Ref<LinkModel> = computed(() => {
            return siteContextService.createProfileLink ?? {} as LinkModel;
        });

        const redirectLink: Ref<LinkModel> = computed(() => {
            return returnLink.url ? returnLink : props.redirectLink;
        });

        const maybeRedirectTitle: Ref<string> = computed(() => {
            return returnTitle.value ? dictionaryService.format(labels.loginReturnTitle, [returnTitle.value]) : props.title;
        });

        const verifyRecaptcha = () => {
            recaptcha.value && recaptcha.value.reset();
            recaptcha.value && recaptcha.value.execute();
        };

        const submit = async({ username, password }): Promise<void> => {
            if (pending.value) {
                return;
            }
            pending.value = true;
            try {
                isResetPasswordState.value
                    ? await reset(username)
                    : await login(username, password);
            } finally {
                pending.value = false;
                showEmailSent.value = false;
            }
        };

        const login = async(username: string, password: string): Promise<LoginResponse | undefined> => {
            const response = await authService.login(username, password, recaptchaKey.value, loginFormWebApiMessageKey);
            if (response?.success) {
                gtmService.login(username);
                authStore.resetAuthentication(response.token, response.refreshToken);
                redirectLink.value?.url && maybeSoftRedirect(redirectLink.value, true);
            }
            return response;
        };

        const reset = async(username: string): Promise<ClientResponse> => {
            const response = await authService.resetPasswordTokenByEmail(username, recaptchaKey.value);
            showEmailSent.value = true;
            await utilities.delay(3000);
            toggleResetPasswordState(false);
            return response;
        };

        const toggleResetPasswordState = (toggled?: boolean): void => {
            pending.value = false;
            isResetPasswordState.value = toggled !== undefined ? toggled : !isResetPasswordState.value;
        };

        return {
            labels,
            submit,
            toggleResetPasswordState,
            isResetPasswordState,
            createProfileLink,
            recaptcha,
            recaptchaKey,
            verifyRecaptcha,
            pending,
            loginFormWebApiMessageKey,
            maybeRedirectTitle,
            showEmailSent,
        };
    },
});
